import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import ControlButtons from './ControlButtons';
import SeatLegend from './SeatLegend';
import { ArrowUp } from 'react-feather';
import {
  SEAT_COLORS,
  SEAT_LABEL_COLORS,
  REFERENCE_TICKET_ID,
  REFERENCE_AREA_LABEL,
} from './seatPalette';
import { fitToSeats, IDENTITY_TRANSFORM } from './fitToSeats';

const SEAT_SIZE = 50;
const SEAT_PADDING = 10;
const TOTAL_SIZE = SEAT_SIZE + SEAT_PADDING * 2;

// Di bawah ukuran ini nomor kursi tidak terbaca dan hanya mengotori warna.
const LABEL_MIN_PIXELS = 16;

// "A" -> 0, "Z" -> 25, "AA" -> 26. Dipakai untuk posisi baris sekaligus tinggi
// grid supaya kursi dengan nama baris dua huruf tidak jatuh di luar viewBox.
const getRowIndex = (row) => {
  let index = 0;
  for (let i = 0; i < row.length; i++) {
    index = index * 26 + (row.charCodeAt(i) - 65 + 1);
  }
  return index - 1; // Kurangi 1 agar indeks dimulai dari 0
};

const Seat = ({ seatData, filter, selectSeatIds }) => {
  // Di-memo supaya rujukan array-nya stabil: keduanya jadi dependensi effect
  // auto-fokus, dan array baru tiap render akan membuatnya berjalan terus.
  const seats = useMemo(
    () => (Array.isArray(seatData) ? seatData : []),
    [seatData],
  );
  const selectedIds = useMemo(
    () => (Array.isArray(selectSeatIds) ? selectSeatIds : []),
    [selectSeatIds],
  );

  const renderableSeats = useMemo(
    () =>
      seats.filter(
        (seat) =>
          seat &&
          typeof seat.x === 'string' &&
          seat.ticket_id !== 9999 &&
          (seat.ticket_id === Number(filter) ||
            seat.ticket_id === REFERENCE_TICKET_ID),
      ),
    [seats, filter],
  );

  // Posisi kotak kursi dalam satuan viewBox — dipakai untuk menggambar,
  // menghitung batas, dan mengarahkan auto-fokus.
  const seatRect = useCallback(
    (seat) => ({
      x: (Number(seat.y) - 1) * TOTAL_SIZE + SEAT_PADDING,
      y: getRowIndex(seat.x) * TOTAL_SIZE + SEAT_PADDING,
    }),
    [],
  );

  // viewBox dipangkas ke batas kursi yang benar-benar digambar. Sebelumnya
  // diambil dari kolom/baris terjauh di seluruh data, sehingga denah tiap
  // kategori mengapung di tengah ruang kosong yang lebar.
  const viewBox = useMemo(() => {
    if (!renderableSeats.length) return null;

    const rects = renderableSeats.map(seatRect);
    const minX = Math.min(...rects.map((r) => r.x));
    const minY = Math.min(...rects.map((r) => r.y));
    const maxX = Math.max(...rects.map((r) => r.x)) + SEAT_SIZE;
    const maxY = Math.max(...rects.map((r) => r.y)) + SEAT_SIZE;

    return {
      x: minX - TOTAL_SIZE,
      y: minY - TOTAL_SIZE,
      w: maxX - minX + TOTAL_SIZE * 2,
      h: maxY - minY + TOTAL_SIZE * 2,
    };
  }, [renderableSeats, seatRect]);

  // Area non-kursi (lidah panggung) dikirim API sebagai puluhan sel terpisah.
  // Sel-sel yang bersebelahan disatukan jadi satu bidang utuh, karena memang
  // satu bangunan — digambar kotak-kotak, bentuknya malah terbaca seperti
  // kursi. Pengelompokan dilakukan per gugus yang bersambungan, jadi kalau
  // API mengirim dua area terpisah keduanya tetap dibedakan.
  const referenceAreas = useMemo(() => {
    const cells = new Map();
    renderableSeats
      .filter((seat) => seat.ticket_id === REFERENCE_TICKET_ID)
      .forEach((seat) => {
        cells.set(`${getRowIndex(seat.x)},${Number(seat.y)}`, seat);
      });
    if (!cells.size) return [];

    const seen = new Set();
    const areas = [];

    cells.forEach((_, startKey) => {
      if (seen.has(startKey)) return;

      const stack = [startKey];
      const cluster = [];
      seen.add(startKey);

      while (stack.length) {
        const key = stack.pop();
        cluster.push(cells.get(key));
        const [row, col] = key.split(',').map(Number);
        [
          [row - 1, col],
          [row + 1, col],
          [row, col - 1],
          [row, col + 1],
        ].forEach(([r, c]) => {
          const neighbour = `${r},${c}`;
          if (cells.has(neighbour) && !seen.has(neighbour)) {
            seen.add(neighbour);
            stack.push(neighbour);
          }
        });
      }

      const rects = cluster.map(seatRect);
      const minX = Math.min(...rects.map((r) => r.x)) - SEAT_PADDING;
      const minY = Math.min(...rects.map((r) => r.y)) - SEAT_PADDING;
      const maxX =
        Math.max(...rects.map((r) => r.x)) + SEAT_SIZE + SEAT_PADDING;
      const maxY =
        Math.max(...rects.map((r) => r.y)) + SEAT_SIZE + SEAT_PADDING;

      areas.push({
        id: startKey,
        x: minX,
        y: minY,
        w: maxX - minX,
        h: maxY - minY,
      });
    });

    return areas;
  }, [renderableSeats, seatRect]);

  // Kursi pelanggan digambar paling akhir agar tidak tertimpa tetangganya.
  // Area non-kursi dikeluarkan — sudah digambar sebagai satu bidang.
  const paintOrder = useMemo(
    () =>
      renderableSeats
        .filter((seat) => seat.ticket_id !== REFERENCE_TICKET_ID)
        .sort(
          (a, b) =>
            Number(selectedIds.includes(a.id)) -
            Number(selectedIds.includes(b.id)),
        ),
    [renderableSeats, selectedIds],
  );

  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const pinchDistanceRef = useRef(null);

  const fittedTransform = useRef(null);

  const [dragging, setDragging] = useState(false);
  const [transform, setTransform] = useState(IDENTITY_TRANSFORM);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Ukuran satu kursi di layar dalam piksel pada transform saat ini.
  const seatPixels =
    viewBox && canvasSize.width && canvasSize.height
      ? SEAT_SIZE *
        Math.min(canvasSize.width / viewBox.w, canvasSize.height / viewBox.h) *
        transform.scale
      : 0;
  const showLabels = seatPixels >= LABEL_MIN_PIXELS;

  // Saat denah dibuka, langsung arahkan dan perbesar ke kursi pelanggan.
  // Tanpa ini pelanggan harus memburu empat kursinya sendiri di antara ratusan
  // kotak sebesar beberapa piksel.
  useEffect(() => {
    if (fittedTransform.current) return undefined;

    const targets = renderableSeats.filter((seat) =>
      selectedIds.includes(seat.id),
    );
    if (!targets.length || !viewBox) return undefined;

    let frame = 0;
    let attempts = 0;

    const fit = () => {
      const box = containerRef.current?.getBoundingClientRect();
      // Modal masih beranimasi masuk dan belum punya ukuran — coba lagi.
      if (!box?.width || !box?.height) {
        if (attempts++ < 20) frame = requestAnimationFrame(fit);
        return;
      }

      const next = fitToSeats({
        viewBox,
        box,
        rects: targets.map(seatRect),
        seatSize: SEAT_SIZE,
      });
      if (!next) return;

      // Disimpan supaya tombol reset mengembalikan ke kursi pelanggan,
      // bukan ke tampilan awal yang membuat mereka tersesat lagi.
      fittedTransform.current = next;
      setTransform(next);
    };

    frame = requestAnimationFrame(fit);
    return () => cancelAnimationFrame(frame);
  }, [renderableSeats, selectedIds, viewBox, seatRect]);

  // Ukuran kanvas diukur supaya nomor kursi bisa disembunyikan saat kursinya
  // masih terlalu kecil untuk dibaca — pada tampilan menyeluruh, label hanya
  // jadi bercak abu-abu yang mengaburkan warna kursi.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const measure = () => {
      const box = el.getBoundingClientRect();
      setCanvasSize({ width: box.width, height: box.height });
    };
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isEventInContainer = useCallback((clientX, clientY) => {
    if (!containerRef.current) return false;
    const rect = containerRef.current.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }, []);

  const startDrag = (x, y) => {
    draggingRef.current = true;
    dragStartRef.current = { x, y };
    setDragging(true);
  };

  const stopDrag = () => {
    draggingRef.current = false;
    pinchDistanceRef.current = null;
    setDragging(false);
  };

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Zoom terhadap satu titik fokus (kursor, atau titik tengah pinch).
  const zoomAt = useCallback((scaleChange, clientX, clientY) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const midX = clientX - rect.left;
    const midY = clientY - rect.top;

    setTransform((prev) => {
      const newScale = Math.max(0.5, Math.min(prev.scale * scaleChange, 10));
      const focusX = (midX - prev.translateX) / prev.scale;
      const focusY = (midY - prev.translateY) / prev.scale;

      return {
        scale: newScale,
        translateX: midX - focusX * newScale,
        translateY: midY - focusY * newScale,
      };
    });
  }, []);

  const handleTouchStart = (e) => {
    if (!e.touches.length) return;
    if (!isEventInContainer(e.touches[0].clientX, e.touches[0].clientY)) return;
    e.preventDefault();

    if (e.touches.length === 1) {
      startDrag(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      pinchDistanceRef.current = getDistance(e.touches[0], e.touches[1]);
    }
  };

  const handleTouchEnd = (e) => {
    e.preventDefault();
    stopDrag();
  };

  // Listener non-passive dipasang sekali saja. Status drag/pinch disimpan di
  // ref supaya effect ini tidak dipasang ulang setiap frame zoom/pan.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const handleWheel = (e) => {
      if (!isEventInContainer(e.clientX, e.clientY)) return;
      e.preventDefault();
      e.stopPropagation();

      const zoomIntensity = 0.1;
      zoomAt(
        e.deltaY < 0 ? 1 + zoomIntensity : 1 - zoomIntensity,
        e.clientX,
        e.clientY,
      );
    };

    const handleTouchMove = (e) => {
      e.preventDefault();

      if (e.touches.length === 1 && draggingRef.current) {
        const slowFactor = 3; // Faktor pengurangan kecepatan drag
        const deltaX =
          (e.touches[0].clientX - dragStartRef.current.x) / slowFactor;
        const deltaY =
          (e.touches[0].clientY - dragStartRef.current.y) / slowFactor;

        setTransform((prev) => ({
          ...prev,
          translateX: prev.translateX + deltaX,
          translateY: prev.translateY + deltaY,
        }));

        dragStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
        };
      } else if (e.touches.length === 2 && pinchDistanceRef.current) {
        const distance = getDistance(e.touches[0], e.touches[1]);
        zoomAt(
          distance / pinchDistanceRef.current,
          (e.touches[0].clientX + e.touches[1].clientX) / 2,
          (e.touches[0].clientY + e.touches[1].clientY) / 2,
        );
        pinchDistanceRef.current = distance;
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isEventInContainer, zoomAt]);

  const handleMouseDown = (e) => {
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    if (
      e.target.tagName === 'svg' ||
      e.target.tagName === 'rect' ||
      e.target.tagName === 'text'
    ) {
      startDrag(e.clientX, e.clientY);
    }
  };

  const handleMouseMove = (e) => {
    if (!draggingRef.current) return;
    if (!isEventInContainer(e.clientX, e.clientY)) return;
    e.preventDefault();

    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;
    setTransform((prev) => ({
      ...prev,
      translateX: prev.translateX + deltaX,
      translateY: prev.translateY + deltaY,
    }));
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = (e) => {
    e.preventDefault();
    stopDrag();
  };

  const colorSeat = (seat) => {
    if (selectedIds.includes(seat.id)) return SEAT_COLORS.yours;
    if (Number(seat.status) !== 1) return SEAT_COLORS.unavailable;
    // Berbayar = ada biaya tambahan sama sekali. Ambang 100.000 sebelumnya
    // salah menandai kursi 50.000 dan 80.000 sebagai gratis.
    return Number(seat.seat_charged) > 0
      ? SEAT_COLORS.paid
      : SEAT_COLORS.available;
  };

  const seatCursor = (seat) =>
    Number(seat.status) === 1 ? 'pointer' : 'not-allowed';

  const handleZoomIn = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.min(prev.scale + 0.1, 10), // Maksimum zoom 10x
    }));
  };

  const handleZoomOut = () => {
    setTransform((prev) => ({
      ...prev,
      scale: Math.max(prev.scale - 0.1, 0.5), // Minimum zoom 0.5x
    }));
  };

  const handleReset = () => {
    setTransform(fittedTransform.current ?? IDENTITY_TRANSFORM);
  };

  return (
    <>
      <SeatLegend />
      <p className="seat-map-hint">
        Denah sudah diarahkan ke kursi Anda (bertanda merah). Cubit atau pakai
        tombol +/− untuk memperbesar, geser untuk berpindah.
      </p>
      <h6 className="seat-map-stage">Panggung</h6>
      <div className="seat-map-frame">
        <div
          ref={containerRef}
          style={{
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {renderableSeats.length === 0 ? (
            <p className="seat-map-empty fw-lighter">
              Denah kursi tidak tersedia untuk kategori ini.
            </p>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="seat-map-canvas"
              viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
              preserveAspectRatio="xMidYMid"
              style={{
                transform: `translate(${transform.translateX}px, ${transform.translateY}px) scale(${transform.scale})`,
                transformOrigin: '0 0',
              }}
            >
              {referenceAreas.map((area) => {
                const words = REFERENCE_AREA_LABEL.toUpperCase().split(' ');
                const longest = Math.max(...words.map((w) => w.length));
                // Muat pada lebar bidang sekaligus tinggi per barisnya.
                const fontSize = Math.min(
                  area.w / (longest * 0.68),
                  ((area.h / words.length) * 0.7) / 1,
                );
                const lineHeight = fontSize * 1.15;
                const midX = area.x + area.w / 2;

                return (
                  <g key={`area-${area.id}`}>
                    <rect
                      x={area.x}
                      y={area.y}
                      width={area.w}
                      height={area.h}
                      rx={10}
                      fill={SEAT_COLORS.reference}
                      stroke="rgba(255, 255, 255, 0.28)"
                      strokeWidth={2}
                    />
                    {fontSize >= 9 && (
                      <text
                        x={midX}
                        y={area.y + area.h / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fill="#fff"
                        fontSize={fontSize}
                        fontWeight="700"
                        letterSpacing={fontSize * 0.08}
                      >
                        {words.map((word, i) => (
                          <tspan
                            key={word}
                            x={midX}
                            dy={
                              i === 0
                                ? (-(words.length - 1) * lineHeight) / 2
                                : lineHeight
                            }
                          >
                            {word}
                          </tspan>
                        ))}
                      </text>
                    )}
                  </g>
                );
              })}

              {paintOrder.map((seat) => {
                const isSelected = selectedIds.includes(seat?.id);
                const { x: rectX, y: rectY } = seatRect(seat);
                const cursor = seatCursor(seat);

                return (
                  <g
                    key={seat.id}
                    data-seat={seat.name}
                    style={{ pointerEvents: 'all' }}
                    onTouchStart={(e) => {
                      // Mencegah event default yang bisa mengganggu
                      e.preventDefault();
                    }}
                  >
                    {/* Lingkar tipis di luar kursi pelanggan. Sebelumnya efek
                        ini dibuat dengan stroke setebal 20 satuan, yang meluber
                        menutupi kursi-kursi di sebelahnya. */}
                    {isSelected && (
                      <rect
                        x={rectX - 8}
                        y={rectY - 8}
                        width={SEAT_SIZE + 16}
                        height={SEAT_SIZE + 16}
                        rx={10}
                        fill="none"
                        stroke={SEAT_COLORS.yours}
                        strokeOpacity="0.45"
                        strokeWidth="5"
                      />
                    )}
                    <rect
                      x={rectX}
                      y={rectY}
                      width={SEAT_SIZE}
                      height={SEAT_SIZE}
                      rx={7}
                      strokeWidth={isSelected ? 4 : 1.5}
                      stroke={isSelected ? '#fff' : 'rgba(0, 0, 0, 0.45)'}
                      fill={colorSeat(seat)}
                      style={{ cursor }}
                    />

                    {showLabels && (
                      <text
                        x={rectX + SEAT_SIZE / 2}
                        y={rectY + SEAT_SIZE / 2}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="16px"
                        fontWeight="700"
                        fill={
                          isSelected
                            ? SEAT_LABEL_COLORS.onDark
                            : SEAT_LABEL_COLORS.onLight
                        }
                        style={{ cursor }}
                      >
                        {seat.name}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          )}
        </div>
      </div>
      <ControlButtons
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
      />
      <div className="seat-map-direction">
        <ArrowUp size={18} aria-hidden="true" />
        <span>Arah hadap kursi</span>
        <ArrowUp size={18} aria-hidden="true" />
      </div>
    </>
  );
};

export default Seat;
